import { Injectable, OnDestroy } from '@angular/core';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { ChatService } from 'src/app/core/backend/common/services/chat.service';
import { FirestoreChatService, FirestoreGroup } from 'src/app/core/services/firestore-chat.service';
import { AuthenticationService } from './auth.service';
import { formatUserWithParentDisplayName } from 'src/app/core/helpers/user-display-name';

export interface ChatNotificationEvent {
    senderName: string;
    content: string;
    created_at: Date;
    chatId: string;
}

/**
 * Singleton bridge service that sets up Firestore real-time listeners for all
 * user chats/groups and emits events consumed by NotificationsComponent.
 * ChatsComponent keeps its own listeners for sidebar badges / Swal toasts.
 */
@Injectable({ providedIn: 'root' })
export class ChatNotificationBridgeService implements OnDestroy {
    private readonly chatUsersLimit = 1000;

    private _message$ = new Subject<ChatNotificationEvent>();
    readonly newMessage$ = this._message$.asObservable();

    private unsubs: Array<() => void> = [];
    private groupsUnsub: (() => void) | null = null;
    private baselines = new Set<string>();
    private prevUnread: Record<string, number> = {};
    private active = false;
    private contextKey: string | null = null;
    private currentUserId = 0;
    private scopedAcademyId = 0;
    private allowedChatIds = new Set<string>();
    private scopedUserIds = new Set<number>();
    private chatParticipantNames = new Map<string, string>();
    private userNamesById = new Map<string, string>();
    private listenerSettledAt = new Map<string, number>();
    private readonly listenerSettleMs = 2000;

    constructor(
        private chatService: ChatService,
        private firestoreChat: FirestoreChatService,
        private authService: AuthenticationService
    ) { }

    startListening(): void {
        this.ensureListening();
    }

    isActive(): boolean {
        return this.active && this.unsubs.length > 0;
    }

    /** Latest unread count per chatId from global Firestore listeners. */
    getUnreadCountsSnapshot(): Readonly<Record<string, number>> {
        return { ...this.prevUnread };
    }

    /**
     * Start or refresh global listeners once permission + academy context exist.
     * Safe to call multiple times — restarts only when the academy context changes.
     */
    ensureListening(): void {
        if (!this.authService.hasPermission('communication.chats.view')) {
            return;
        }

        const academyId = this.resolveAcademyContextId();
        if (!academyId) {
            return;
        }

        const nextContextKey = String(academyId);
        if (this.active && this.contextKey === nextContextKey && this.unsubs.length > 0) {
            return;
        }

        this.stopListening();
        this.contextKey = nextContextKey;
        this.scopedAcademyId = academyId;
        this.active = true;

        forkJoin({
            players: this.chatService.apiV1ChatsAvailableUsersGet({
                user_type: 'players',
                limit: this.chatUsersLimit,
            }).pipe(catchError(() => of(null))),
            admins: this.chatService.apiV1ChatsAvailableUsersGet({
                user_type: 'admins',
                limit: this.chatUsersLimit,
            }).pipe(catchError(() => of(null))),
        }).pipe(take(1)).subscribe(({ players, admins }) => {
            if (!this.active || this.contextKey !== nextContextKey) {
                return;
            }

            const playersRes = players as { data?: { users?: any[]; current_user_id?: number } } | null;
            const adminsRes = admins as { data?: { users?: any[]; current_user_id?: number } } | null;

            if (!playersRes && !adminsRes) {
                this.stopListening();
                return;
            }

            this.currentUserId =
                playersRes?.data?.current_user_id
                || adminsRes?.data?.current_user_id
                || 0;

            if (!this.currentUserId) {
                this.stopListening();
                return;
            }

            this.wireUsersListeners(playersRes?.data?.users || [], this.currentUserId);
            this.wireUsersListeners(adminsRes?.data?.users || [], this.currentUserId);
            this.listenGroups();

            if (this.unsubs.length === 0) {
                this.stopListening();
            }
        });
    }

    stopListening(): void {
        this.active = false;
        this.contextKey = null;
        this.scopedAcademyId = 0;
        this.unsubs.forEach(u => u());
        this.unsubs = [];
        if (this.groupsUnsub) {
            this.groupsUnsub();
            this.groupsUnsub = null;
        }
        this.baselines.clear();
        this.prevUnread = {};
        this.allowedChatIds.clear();
        this.scopedUserIds.clear();
        this.currentUserId = 0;
        this.chatParticipantNames.clear();
        this.userNamesById.clear();
        this.listenerSettledAt.clear();
    }

    ngOnDestroy(): void {
        this.stopListening();
        this._message$.complete();
    }

    private resolveAcademyContextId(): number {
        return this.authService.getActiveAcademyScopeId() ?? 0;
    }

    private wireUsersListeners(users: any[], currentUserId: number): void {
        if (!currentUserId) {
            return;
        }

        for (const user of users) {
            const chatId: string = user.chat_id;
            if (!chatId) {
                continue;
            }
            if (this.baselines.has(`${chatId}_wired`)) {
                continue;
            }
            this.baselines.add(`${chatId}_wired`);
            this.registerScopedUser(user);
            this.registerChatParticipant(user);

            const unsub = this.firestoreChat.listenChatSummary(chatId, currentUserId, (summary) => {
                if (!this.canNotifyChat(chatId)) {
                    return;
                }

                if (!this.baselines.has(chatId)) {
                    this.baselines.add(chatId);
                    this.prevUnread[chatId] = summary.unreadCount;
                    this.listenerSettledAt.set(chatId, Date.now());
                    return;
                }

                if (!this.isListenerSettled(chatId)) {
                    this.prevUnread[chatId] = summary.unreadCount;
                    return;
                }

                if (summary.unreadCount > (this.prevUnread[chatId] ?? 0)) {
                    this.prevUnread[chatId] = summary.unreadCount;
                    this._message$.next({
                        senderName: this.resolveSenderName(chatId, summary),
                        content: summary.content,
                        created_at: new Date(),
                        chatId,
                    });
                } else {
                    this.prevUnread[chatId] = summary.unreadCount;
                }
            });
            this.unsubs.push(unsub);
        }
    }

    private listenGroups(): void {
        if (this.groupsUnsub || !this.currentUserId) {
            return;
        }

        this.groupsUnsub = this.firestoreChat.loadGroups((groups: FirestoreGroup[]) => {
            for (const g of groups) {
                const chatId = `group_${g.firestore_id}`;
                if (this.baselines.has(`${chatId}_wired`)) {
                    continue;
                }
                this.baselines.add(`${chatId}_wired`);
                this.allowedChatIds.add(chatId);

                const unsub = this.firestoreChat.listenChatSummary(chatId, this.currentUserId, (summary) => {
                    if (!this.canNotifyChat(chatId)) {
                        return;
                    }

                    if (!this.baselines.has(chatId)) {
                        this.baselines.add(chatId);
                        this.prevUnread[chatId] = summary.unreadCount;
                        this.listenerSettledAt.set(chatId, Date.now());
                        return;
                    }

                    if (!this.isListenerSettled(chatId)) {
                        this.prevUnread[chatId] = summary.unreadCount;
                        return;
                    }

                    if (summary.unreadCount > (this.prevUnread[chatId] ?? 0)) {
                        this.prevUnread[chatId] = summary.unreadCount;
                        this._message$.next({
                            senderName: this.resolveSenderName(chatId, summary),
                            content: summary.content,
                            created_at: new Date(),
                            chatId,
                        });
                    } else {
                        this.prevUnread[chatId] = summary.unreadCount;
                    }
                });
                this.unsubs.push(unsub);
            }
        }, this.buildGroupFilter());
    }

    private buildGroupFilter(): { academyId?: number; memberUserIds?: number[] } {
        if (!this.scopedAcademyId) {
            return {};
        }

        return {
            academyId: this.scopedAcademyId,
            memberUserIds: [...this.scopedUserIds],
        };
    }

    private canNotifyChat(chatId: string): boolean {
        return this.allowedChatIds.has(chatId);
    }

    private registerScopedUser(user: any): void {
        const chatId = String(user?.chat_id ?? '').trim();
        if (chatId) {
            this.allowedChatIds.add(chatId);
        }

        const userId = Number(user?.id ?? 0);
        if (userId > 0) {
            this.scopedUserIds.add(userId);
        }
    }

    private registerChatParticipant(user: any): void {
        const chatId = String(user?.chat_id ?? '').trim();
        if (!chatId) {
            return;
        }

        const displayName = formatUserWithParentDisplayName(user?.name, user?.parent_name)
            || String(user?.name ?? '').trim();
        if (displayName) {
            this.chatParticipantNames.set(chatId, displayName);
        }

        const userId = String(user?.id ?? '').trim();
        if (userId && displayName) {
            this.userNamesById.set(userId, displayName);
        }
    }

    private resolveSenderName(
        chatId: string,
        summary: { senderName: string; senderId: string },
    ): string {
        const fromMessage = String(summary.senderName ?? '').trim();
        if (fromMessage) {
            return fromMessage;
        }

        const senderId = String(summary.senderId ?? '').trim();
        if (senderId && this.userNamesById.has(senderId)) {
            return this.userNamesById.get(senderId)!;
        }

        if (this.chatParticipantNames.has(chatId)) {
            return this.chatParticipantNames.get(chatId)!;
        }

        return 'المستخدم';
    }

    private isListenerSettled(chatId: string): boolean {
        const settledAt = this.listenerSettledAt.get(chatId) ?? 0;
        return settledAt > 0 && (Date.now() - settledAt) >= this.listenerSettleMs;
    }
}
